import './Device.scss';
import Facilities from './Facilities/Facilities';
import FacilityCareAreas from './FacilityCareAreas/FacilityCareAreas';
import { ADMINISTRATION, AUTHOR } from 'constants/roles';
import { useDispatch, useSelector } from 'react-redux';
import { Facility } from 'interfaces/facility';
import { useEffect, useMemo } from 'react';
import { CareArea } from 'interfaces/care-area';
import CareAreas from './CareAreas/CareAreas';
import ListDrugs from './ListDrugs/ListDrugs';
import Drugs from './Drugs/Drugs';
import { DrugConcentration } from 'interfaces/drug-concentration';
import { Drug } from 'interfaces/drug';
import { selectUser } from 'store/slices/auth';
import {
  deviceActions,
  selectSelectedFacility,
  selectSelectedCareArea,
  selectSelectedDrugConcentration,
  selectSelectedDrug,
  selectIsAddingCareAreas,
  selectIsAddingConcentration,
} from 'store/slices/device';
import ConcentrationForm from './ConcentrationForm/ConcentrationForm';
import DrugForm from './DrugForm/DrugForm';

/**
 * @description Device component that manages the display and interaction with facilities, care areas, drugs, and drug concentrations.
 * It provides a hierarchical navigation structure and conditional rendering of different forms and lists based on user selection.
 *
 * @SADtrace SAD-003-4, SAD-003-4.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {JSX.Element} The rendered Device component
 */
const Device = () => {
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const canManage = [ADMINISTRATION, AUTHOR].includes(user?.role || '');
  const facility: Facility | null = useSelector(selectSelectedFacility);
  const careArea: CareArea | null = useSelector(selectSelectedCareArea);
  const drug: Drug | null = useSelector(selectSelectedDrug);
  const drugConcentration: DrugConcentration | null = useSelector(
    selectSelectedDrugConcentration,
  );
  const isAddingCareAreas: boolean = useSelector(selectIsAddingCareAreas);
  const isAddingConcentration: boolean = useSelector(
    selectIsAddingConcentration,
  );

  /**
   * Cleanup effect that resets the device state when the component unmounts
   *
   * @returns {Function} Cleanup function that dispatches the reset action
   */
  useEffect(() => {
    return () => {
      dispatch(deviceActions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @description Constructs a hierarchical title for the content area based on selected facility and care area
   *
   * @returns {string} The formatted title string showing the navigation path
   */
  const contentTitle = useMemo(() => {
    let title = '';
    if (facility) {
      title = facility.name;
    }

    if (facility && careArea) {
      title += ' > ' + careArea.name;
    }

    return title;
  }, [facility, careArea]);

  return (
    <div className="d-flex device">
      <div className="device__sidebar">
        <Facilities canManage={canManage} />
        {facility ? (
          <FacilityCareAreas
            facility={facility}
            canManage={canManage}
            disabledAdding={isAddingCareAreas}
          />
        ) : null}
      </div>
      <div className="device__drugs">
        <Drugs
          drug={drug}
          drugConcentration={drugConcentration}
          careArea={careArea}
          facility={facility}
          disabledAdding={!facility || !careArea || isAddingConcentration}
        />
      </div>
      <div className="device__content">
        <h4>{contentTitle}</h4>
        {isAddingCareAreas && facility ? (
          <CareAreas canManage={canManage} facility={facility} />
        ) : null}
        {isAddingConcentration && facility && careArea ? (
          <ListDrugs
            canManage={canManage}
            careArea={careArea}
            facility={facility}
          />
        ) : null}
        {drugConcentration ? (
          <ConcentrationForm concentration={drugConcentration} />
        ) : null}
        {!!drug && !!careArea && !!facility ? (
          <DrugForm drug={drug} facility={facility} />
        ) : null}
      </div>
    </div>
  );
};

export default Device;
